/**
 * Booking time from the job screen: the small pure pieces.
 */

import { ApiError } from "./api";
import type { JobTimeOut } from "./types";

export const MAX_MINUTES = 1440;

/** Whole minutes, 1..1440, or null. Anything else is not sent. */
export function parseMinutes(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n >= 1 && n <= MAX_MINUTES ? n : null;
}

/** 90 -> "1 hr 30 min", 60 -> "1 hr", 15 -> "15 min". */
export function durationLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

/** Newest first; entries with no start time sink to the bottom. */
export function newestFirst(entries: JobTimeOut[]): JobTimeOut[] {
  return [...entries].sort((a, b) => {
    const at = a.started_at ? Date.parse(a.started_at) : Number.NEGATIVE_INFINITY;
    const bt = b.started_at ? Date.parse(b.started_at) : Number.NEGATIVE_INFINITY;
    return bt - at;
  });
}

/**
 * The words for a refused booking. The over-hours / variation gate answers
 * 409 with `{ detail: { block, detail } }`, so the message a person should
 * read is one level further down than `ApiError.message`.
 */
export function bookingError(err: unknown): string {
  if (!(err instanceof ApiError)) return "Couldn't book that time.";
  let d: unknown = err.detail;
  if (
    d &&
    typeof d === "object" &&
    (d as { detail?: unknown }).detail &&
    typeof (d as { detail?: unknown }).detail === "object"
  ) {
    d = (d as { detail: unknown }).detail;
  }
  if (d && typeof d === "object" && typeof (d as { detail?: unknown }).detail === "string") {
    return (d as { detail: string }).detail;
  }
  return err.message;
}
